import { Provider as AnalyticsProvider } from '@carvana/analytics';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import React, { useContext, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import styled, { ThemeProvider, css } from 'styled-components';
import { Dashboard, Step, Summary } from '|/app/services';
import MobileNavigationRail from '|/app/steps/navigation-rail/mobile-navigation-rail';
import OrderSummary from '|/app/steps/order-summary';
import routeNameComponentLookup from '|/app/steps/routes.config';
import theme from '|/components/base/theme';
import AppFrameworkContext, {
  AppFrameworkContextProvider
} from '|/helpers/app-framework';
import { UIProvider } from '~/library/helpers/src/uiprovider';
import AnalyticsConfig from '~/library/store/src/analytics-config';
import libraryTheme from '~/library/styles/src/theme';
import { StepUrlsContextProvider } from './step-urls-context';
import ProtectedRoute from '../protect-route';
import SummaryContext from '../summary-context';

const StepContainer = styled.div`
  display: grid;

  @media (min-width: ${theme.breakpoints.sm}) {
    grid-template-columns: min-content 1fr;
  }
`;

const HideableStep = styled.div<{ isHidden: boolean }>`
  ${props =>
    props.isHidden &&
    css`
      display: none;
    `}
`;

const defaultProps = {
  dashboard: null as null | Dashboard,
  summary: null as null | Summary,
  ReloadDashboard: () => Promise.resolve(),
  GetDashboardAndRouteToNextStep: () => void 0
};

const getLatestStep = (steps: Step[]) => {
  const orderedSteps = steps.sort((a, b) => a.order - b.order);
  const latestStep = orderedSteps.find(
    step => step.isEnabled && !step.isComplete
  );
  return latestStep ?? orderedSteps[0];
};

const defaultStepProps = {
  step: null as null | Step,
  latestStep: null as null | Step,
  isHidden: false,
  ReloadDashboard: () => Promise.resolve(),
  GetDashboardAndRouteToNextStep: () => {}
};

const StepElement = ({
  step,
  latestStep,
  isHidden,
  ReloadDashboard,
  GetDashboardAndRouteToNextStep
} = defaultStepProps) => {
  const { id = null, section = 0, order = 0, isEnabled = false } = step || {};
  const { section: latestSection = 0, order: latestOrder = 0 } =
    latestStep || {};
  const { trackEvent } = useContext(AnalyticsContext);

  const stepConfig = id === null ? null : routeNameComponentLookup[id];
  const activeSection = latestSection;

  const shouldRedirect =
    id === null ||
    stepConfig === null ||
    section !== activeSection ||
    order > latestOrder ||
    !isEnabled;

  useEffect(() => {
    if (!shouldRedirect) trackEvent('pp_step_started', { stepName: id });
  }, [trackEvent, shouldRedirect, id]);

  if (shouldRedirect) {
    return <Navigate to="/" />;
  }

  return (
    <ProtectedRoute>
      <HideableStep isHidden={isHidden}>
        <stepConfig.component
          GetDashboardAndRouteToNextStep={() => ({
            type: 'GET_DASHBOARD',
            payload: GetDashboardAndRouteToNextStep
          })}
          GetDashboardBackgroundLoading={() => ({
            type: 'GET_DASHBOARD_NO_LOADING_INDICATOR',
            payload: ReloadDashboard
          })}
        />
      </HideableStep>
    </ProtectedRoute>
  );
};

const StepLayoutContainer = styled.div`
  padding: 2rem 1.5rem;
  margin-bottom: 200px;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;

  @media (min-width: ${theme.breakpoints.sm}) {
    padding: 80px 3% 0;
  }

  & > *:first-child,
  & > *:last-child {
    grid-column: 1;
    grid-row: 1;
  }
`;

const Steps = ({
  dashboard,
  summary,
  ReloadDashboard,
  GetDashboardAndRouteToNextStep
} = defaultProps) => {
  const { steps = [] } = dashboard || {};
  const { load } = useContext(AppFrameworkContext);
  const { showSummary } = useContext(SummaryContext);

  useEffect(() => {
    if (dashboard === null) {
      load(ReloadDashboard());
    }
  }, [dashboard, ReloadDashboard, load]);

  if (steps.length === 0) return null;

  const latestStep = getLatestStep(steps);

  return (
    <StepContainer>
      <MobileNavigationRail dashboard={dashboard} summary={summary} />
      <StepLayoutContainer>
        <StepUrlsContextProvider steps={steps}>
          <AppFrameworkContextProvider>
            <AnalyticsProvider configs={AnalyticsConfig}>
              <ThemeProvider theme={libraryTheme}>
                <UIProvider>
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Outlet />
                        </ProtectedRoute>
                      }
                    >
                      {steps.map(step => {
                        return (
                          <Route
                            path={`${step.url}/*`}
                            key={step.name}
                            element={
                              <StepElement
                                step={step}
                                latestStep={latestStep}
                                isHidden={showSummary}
                                ReloadDashboard={ReloadDashboard}
                                GetDashboardAndRouteToNextStep={
                                  GetDashboardAndRouteToNextStep
                                }
                              />
                            }
                          />
                        );
                      })}
                      <Route
                        path="*"
                        element={
                          <ProtectedRoute>
                            <Navigate to="/next-step" replace={true} />
                          </ProtectedRoute>
                        }
                      />
                    </Route>
                  </Routes>
                </UIProvider>
              </ThemeProvider>
            </AnalyticsProvider>
          </AppFrameworkContextProvider>
          <OrderSummary orderSummary={summary} />
        </StepUrlsContextProvider>
      </StepLayoutContainer>
    </StepContainer>
  );
};

export default Steps;
